import { useMemo } from 'react';

import { getUsersLOV, type UserSummary } from 'api/users/endpoints/getUsersLOV';

import { EditReference } from './EditReference';

interface Props {
  // The stored owner_user id (a config value), or null for "no owner". A string because the config
  // has always stored the id as a string (the old <Form.Select> option value); numeric ids coerce.
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (u: UserSummary) => u.user_id;
// `users.username` is a nullable column; `?? ''` keeps the blank label a null produced before the declaration was
// corrected. The row stays selectable either way — `getKey` is the id, not the name.
const getLabel = (u: UserSummary): string => u.username ?? '';

/**
 * Searchable dropdown for users keyed on `user_id` — the id↔display-name expansion is internal: the
 * caller works in the stored id, and the eager LOV resolves it to a username via `findByKey` (the user
 * universe is small, so no server `resolve` is needed). Selection returns the id as a string, matching
 * how the config has always stored it.
 */
export const EditUserReference = ({ value, onChange, label = 'Owner User', mandatory, disabled }: Props) => {
  // Inflate the stored id to the partial summary EditReference selects on; the empty name defers label
  // resolution to the eager LOV's findByKey. Memoised on the id so the reference model isn't rebuilt each render.
  const selected = useMemo<UserSummary | null>(() => (value ? { user_id: Number(value), username: '' } : null), [value]);

  return (
    <EditReference<UserSummary>
      label={label}
      value={selected}
      onChange={(u) => onChange(u !== null ? String(u.user_id) : null)}
      source={getUsersLOV}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
    />
  );
};
